import { useCallback } from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import {
  DeliveryStatus,
  IRestaurantNode,
  IStoreLocationInput,
  ServiceMode,
  useDeliveryRestaurantLazyQuery,
  useGetDeliveryRestaurantsLazyQuery,
  useGetFirstQuoteLazyQuery,
} from 'generated/graphql-gateway';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { convertMilesToMeters } from 'utils/distance';
import { platform } from 'utils/environment';
import { IPlaceAddress } from 'utils/geolocation';

import { SelectedDeliveryAddress } from '../pages/delivery-store-selector/types';
import { logDeliveryAddressSubmitted, logDeliveryStoreAssigned } from '../utils';

export type DeliveryAddressInput = SelectedDeliveryAddress & {
  alias: string;
  isNewAddress: boolean;
};

export enum SelectDeliveryStoreStatus {
  QUOTE_ERROR = 'QUOTE_ERROR',
  QUOTE_UNAVAILABLE = 'QUOTE_UNAVAILABLE',
  QUOTE_SUCCESSFUL = 'QUOTE_SUCCESSFUL',
  UNRECOGNIZED_ADDRESS = 'UNRECOGNIZED_ADDRESS',
}

function getFailureReason(storeId: string | null | undefined, hasErrors: boolean) {
  let failureReason;
  if (hasErrors) {
    failureReason = 'Delivery restaurant API fail';
  } else if (!storeId) {
    failureReason = 'Restaurant not found';
  }
  return failureReason;
}

function useGetCoordinates() {
  const getPlaceIdDetails = usePlaceIdDetails();

  return useCallback(
    async (deliveryAddress: DeliveryAddressInput) => {
      const { coordinates: selectedCoordinates, placeId: selectedPlaceId } = deliveryAddress;

      const placeId = selectedPlaceId;

      let coordinates;
      if (selectedCoordinates) {
        coordinates = selectedCoordinates;
      } else {
        const placeDetails = await getPlaceIdDetails(placeId);
        coordinates = placeDetails?.coordinates;
      }
      return coordinates;
    },
    [getPlaceIdDetails]
  );
}

export const useGetDeliveryRestaurant = () => {
  const enableFirstQuoteV2 = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_FIRST_QUOTE_V2);
  const [queryDeliveryRestaurant] = useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });
  const [getFirstQuote] = useGetFirstQuoteLazyQuery({ fetchPolicy: 'no-cache' });
  const [getDeliveryRestaurants] = useGetDeliveryRestaurantsLazyQuery({ fetchPolicy: 'no-cache' });

  return useCallback(
    async ({
      address,
      deliveryRadius,
      coordinates,
      phoneNumber,
    }: {
      address: IPlaceAddress;
      deliveryRadius: number;
      coordinates: ILocation;
      phoneNumber: string;
    }) => {
      if (!enableFirstQuoteV2) {
        const result = await queryDeliveryRestaurant({
          variables: {
            dropoff: {
              ...address,
              latitude: coordinates.lat,
              longitude: coordinates.lng,
              phoneNumber,
            },
            searchRadius: convertMilesToMeters(deliveryRadius),
            platform: platform(),
          },
        });

        return {
          data: {
            quote: result.data?.deliveryRestaurant.quote,
            restaurant: result.data?.deliveryRestaurant.restaurant,
            deliverySurchargeFeeCents: result.data?.deliveryRestaurant.deliverySurchargeFeeCents,
            quoteId: result.data?.deliveryRestaurant.quoteId,
            storeId: result.data?.deliveryRestaurant.restaurant?.storeId,
          },
          error: result.error,
        };
      }

      const deliveryRestaurantsResult = await getDeliveryRestaurants({
        variables: {
          input: {
            coordinates: {
              userLat: coordinates.lat,
              userLng: coordinates.lng,
              searchRadius: deliveryRadius,
            },
          },
        },
      });

      if (deliveryRestaurantsResult.error) {
        return {
          data: { quote: DeliveryStatus.QUOTE_UNAVAILABLE },
          error: deliveryRestaurantsResult.error,
        };
      }

      const deliveryStores = deliveryRestaurantsResult.data?.deliveryRestaurants?.nodes ?? [];
      if (deliveryStores.length === 0) {
        return {
          data: { quote: DeliveryStatus.QUOTE_UNAVAILABLE },
          error: { message: 'Delivery not available for current address' },
        };
      }

      const storeAddresses: IStoreLocationInput[] = deliveryStores
        .filter(deliveryStore => !!deliveryStore.latitude && !!deliveryStore.longitude)
        .map(deliveryStore => ({
          storeId: deliveryStore.storeId!,
          phoneNumber: deliveryStore.phoneNumber!,
          coordinates: {
            latitude: deliveryStore.latitude as number,
            longitude: deliveryStore.longitude as number,
          },
          physicalAddress: deliveryStore.physicalAddress,
        }));

      const firstQuoteResult = await getFirstQuote({
        variables: {
          dropoff: {
            ...address,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            phoneNumber,
          },
          storeAddresses,
          platform: platform(),
        },
      });

      const storeId = firstQuoteResult.data?.firstQuote.storeId;
      const restaurant = deliveryStores.find(deliveryStore => deliveryStore.storeId === storeId);

      return {
        data: {
          quote: firstQuoteResult.data?.firstQuote.quote,
          restaurant,
          deliverySurchargeFeeCents: firstQuoteResult.data?.firstQuote.deliverySurchargeFeeCents,
          quoteId: firstQuoteResult.data?.firstQuote.quoteId,
          storeId: firstQuoteResult.data?.firstQuote.storeId,
        },
        error: firstQuoteResult.error,
      };
    },
    [enableFirstQuoteV2, getDeliveryRestaurants, getFirstQuote, queryDeliveryRestaurant]
  );
};

export const useSelectDeliveryStore = () => {
  const getCoordinates = useGetCoordinates();
  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const getDeliveryRestaurant = useGetDeliveryRestaurant();

  const { setDeliveryAddress, setQuoteId } = useOrderContext();
  const { setDeliverySurchargeFee, selectServiceMode } = useServiceModeContext();

  return useCallback(
    async (
      deliveryAddress: DeliveryAddressInput
    ): Promise<{
      restaurant?: IRestaurant | null;
      status: SelectDeliveryStoreStatus;
      phoneNumber?: string;
    }> => {
      const { address, phoneNumber, alias, isNewAddress } = deliveryAddress;

      const coordinates = await getCoordinates(deliveryAddress);

      if (!coordinates) {
        return {
          status: SelectDeliveryStoreStatus.UNRECOGNIZED_ADDRESS,
        };
      }

      const result = await getDeliveryRestaurant({
        address,
        deliveryRadius,
        coordinates,
        phoneNumber,
      });

      const deliveryRestaurant = result.data;
      if (deliveryRestaurant) {
        const storeId = deliveryRestaurant.restaurant?.storeId;
        const failureReason = getFailureReason(storeId, !!result.error);

        logDeliveryAddressSubmitted({ storeId, failureReason, isNewAddress });

        const { quote, restaurant, deliverySurchargeFeeCents, quoteId } = deliveryRestaurant;

        if (!quote) {
          return {
            status: SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE,
          };
        }

        if (quote !== DeliveryStatus.QUOTE_SUCCESSFUL) {
          switch (quote) {
            case DeliveryStatus.QUOTE_ERROR:
              return {
                status: SelectDeliveryStoreStatus.QUOTE_ERROR,
              };
            case DeliveryStatus.QUOTE_UNAVAILABLE:
              return {
                status: SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE,
              };
            default:
              return {
                status: SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE,
              };
          }
        }

        logDeliveryStoreAssigned(restaurant as IRestaurantNode);

        if (deliverySurchargeFeeCents) {
          setDeliverySurchargeFee(deliverySurchargeFeeCents);
        }
        setQuoteId(quoteId ?? '');

        setDeliveryAddress({
          ...address,
          shouldSave: false,
          alias,
          latitude: coordinates?.lat ?? undefined,
          longitude: coordinates?.lng ?? undefined,
        });

        selectServiceMode(ServiceMode.DELIVERY);

        return {
          status: SelectDeliveryStoreStatus.QUOTE_SUCCESSFUL,
          phoneNumber,
          restaurant: restaurant as IRestaurant,
        };
      }

      return {
        restaurant: null,
        status: SelectDeliveryStoreStatus.QUOTE_UNAVAILABLE,
        phoneNumber,
      };
    },
    [
      deliveryRadius,
      getCoordinates,
      getDeliveryRestaurant,
      selectServiceMode,
      setDeliveryAddress,
      setDeliverySurchargeFee,
      setQuoteId,
    ]
  );
};
